// ==================== COSTANTI PDF CONDIVISE ====================

export const PDF_CONSTANTS = {
  PAGE_WIDTH: 210,        // A4 width in mm
  PAGE_HEIGHT: 297,       // A4 height in mm
  MARGIN: 15,
  get USABLE_WIDTH() { return this.PAGE_WIDTH - (2 * this.MARGIN); }, // 180mm
  HEADER_HEIGHT: 12,      // Altezza header colorato di un gruppo
  DETAILS_TABLE_HEADER: 12, // Header tabella dettagli ("Dettagli Tecnici Completi")
  STANDARD_CABLE_DETAIL_HEIGHT: 8,   // Altezza dettagli cavo standard (una riga)
  MULTIPOLAR_HEADER_HEIGHT: 8,       // Header cavo multipolare nei dettagli
  MULTIPOLAR_TABLE_HEADER: 6,        // Header tabella poli
  MULTIPOLAR_POLE_HEIGHT: 10,        // Altezza per ogni polo nella tabella dettagli (export usa 8 nel render)
  MULTIPOLAR_SEPARATOR: 2,           // Separatore dopo tabella multipolare
  SVG_PADDING: 4,                    // Padding tra SVG consecutivi
  SVG_BASE_WIDTH: 800,               // Larghezza base degli SVG nel DOM
  GROUP_BOTTOM_SPACING: 8,           // Spazio dopo un gruppo
  // Altezze massime per paginazione
  // NOTA: La prima pagina è riservata ai riepiloghi (cavi/terminali/guaine),
  // quindi i gruppi iniziano SEMPRE dalla seconda pagina.
  // Usare MAX_GROUP_HEIGHT_OTHER_PAGES per la divisione dei gruppi.
  get MAX_GROUP_HEIGHT_FIRST_PAGE() { return this.PAGE_HEIGHT - (this.MARGIN * 2) - 40; },  // ~227mm (NON usare per gruppi cavi)
  get MAX_GROUP_HEIGHT_OTHER_PAGES() { return this.PAGE_HEIGHT - (this.MARGIN * 2); },      // ~267mm (usare per gruppi cavi)
  // Nested/Sheath
  MAX_HEIGHT_NESTED: 220,            // Altezza massima per nodi nested
};

// ==================== FUNZIONI DI SCALA ====================

/**
 * Calcola il fattore di scala per convertire le dimensioni SVG in mm PDF
 */
export const getScale = (usableWidth = PDF_CONSTANTS.USABLE_WIDTH) => {
  return (usableWidth - 6) / PDF_CONSTANTS.SVG_BASE_WIDTH;
};

// ==================== STIME ALTEZZA PER TIPO DI CAVO ====================

/**
 * Stima altezza SVG di un cavo standard (fallback quando il DOM non è disponibile)
 * L'altezza 250 era troppo conservativa. 180 è più realistico per la maggior parte degli SVG standard.
 */
export const estimateStandardSvgHeight = () => {
  return 180; // px — ridotta rispetto ai precedenti 250
};

/**
 * Stima altezza SVG di un cavo multipolare
 */
export const estimateMultipolarSvgHeight = (poleCount) => {
  return Math.max(200, 80 + (poleCount * 60));
};

/**
 * Stima altezza SVG di un gruppo guaina (cavi singoli che condividono un SVG)
 * Formula calibrata sull'SVG effettivo di SheathCableVisualization
 */
export const estimateSheathSvgHeight = (cableCount) => {
  return Math.max(200, 80 + (cableCount * 70));
};

/**
 * Stima altezza SVG di un gruppo di cavi multipli (stessa siglatura, SVG condiviso)
 */
export const estimateMultipleGroupSvgHeight = () => {
  return 180; // px — ridotta rispetto ai precedenti 250
};

// ==================== ALTEZZA COMPLETA PER TIPO ====================

/**
 * Calcola altezza totale stimata di un cavo standard (SVG + dettagli)
 */
export const estimateStandardCableHeight = (scale) => {
  const svgHeight = estimateStandardSvgHeight();
  return (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING + PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT;
};

/**
 * Calcola altezza totale stimata di un cavo multipolare (SVG + header + tabella poli)
 */
export const estimateMultipolarCableHeight = (cable, scale) => {
  const poleCount = cable.poli?.length || 0;
  const svgHeight = estimateMultipolarSvgHeight(poleCount);
  const svgScaled = (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
  const detailsHeight = PDF_CONSTANTS.MULTIPOLAR_HEADER_HEIGHT +
    PDF_CONSTANTS.MULTIPOLAR_TABLE_HEADER +
    (poleCount * PDF_CONSTANTS.MULTIPOLAR_POLE_HEIGHT) +
    PDF_CONSTANTS.MULTIPOLAR_SEPARATOR;
  return svgScaled + detailsHeight;
};

/**
 * Calcola altezza totale stimata di un gruppo guaina (SVG condiviso + dettagli per ogni cavo)
 */
export const estimateSheathGroupHeight = (cables, scale) => {
  const svgHeight = estimateSheathSvgHeight(cables.length);
  return (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING + (cables.length * PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT);
};

/**
 * Calcola altezza totale stimata di un gruppo multiplo (SVG condiviso + dettagli per ogni cavo)
 */
export const estimateMultipleGroupHeight = (cables, scale) => {
  const svgHeight = estimateMultipleGroupSvgHeight();
  return (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING + (cables.length * PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT);
};

// ==================== ALTEZZA SOTTO-GRUPPO LOGICO ====================

/**
 * Stima l'altezza di un sotto-gruppo logico (come prodotto da analyzeAndGroupCables).
 * Questo è il sostituto diretto delle formule inline in divideGroupsIntoSubgroups.
 */
export const estimateLogicalSubGroupHeight = (logicalSubGroup, scale) => {
  if (logicalSubGroup.isSingle) {
    const cable = logicalSubGroup.cables[0];
    if (cable.poli && Array.isArray(cable.poli)) {
      return estimateMultipolarCableHeight(cable, scale);
    } else {
      return estimateStandardCableHeight(scale);
    }
  } else {
    // Cavi multipli condividono l'SVG
    return estimateMultipleGroupHeight(logicalSubGroup.cables, scale);
  }
};

// ==================== ALTEZZA TOTALE GRUPPO ====================

/**
 * Stima l'altezza totale di un gruppo dai suoi sotto-gruppi logici (output di analyzeAndGroupCables).
 * Sostituisce i calcoli inline duplicati in divideGroupsIntoSubgroups.
 *
 * @param {Array} logicalSubGroups - Output di analyzeAndGroupCables
 * @param {number} scale - Fattore di scala
 * @param {boolean} isSheathGroup - Se true, i cavi singoli condividono un unico SVG (guaina)
 */
export const estimateGroupHeightFromLogicalSubGroups = (logicalSubGroups, scale, isSheathGroup = false) => {
  // HEADER_HEIGHT = header colorato del gruppo
  // DETAILS_TABLE_HEADER = header tabella "Dettagli Tecnici Completi" (appare una volta per gruppo)
  let totalHeight = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;

  if (isSheathGroup) {
    // In una guaina, i cavi singoli standard condividono UN UNICO SVG.
    // Separa singles da altri tipi per calcolare l'altezza corretta.
    const singleStandardCables = [];
    const otherSubGroups = [];

    logicalSubGroups.forEach(subGroup => {
      if (subGroup.isSingle) {
        const cable = subGroup.cables[0];
        if (cable.poli && Array.isArray(cable.poli)) {
          // Multipolare: ha il suo SVG separato
          otherSubGroups.push(subGroup);
        } else {
          // Standard singolo: condivide SVG con gli altri singles nella guaina
          singleStandardCables.push(cable);
        }
      } else {
        // Gruppo multiplo: ha il suo SVG separato
        otherSubGroups.push(subGroup);
      }
    });

    // SVG condiviso per tutti i cavi singoli standard nella guaina
    if (singleStandardCables.length > 0) {
      totalHeight += estimateSheathGroupHeight(singleStandardCables, scale);
    }

    // SVG separati per multipolari e gruppi multipli
    otherSubGroups.forEach(subGroup => {
      totalHeight += estimateLogicalSubGroupHeight(subGroup, scale);
    });
  } else {
    logicalSubGroups.forEach(subGroup => {
      totalHeight += estimateLogicalSubGroupHeight(subGroup, scale);
    });
  }

  return totalHeight;
};

// ==================== ALTEZZA RENDERABLE (PER NESTED) ====================

/**
 * Stima l'altezza di un elemento renderizzabile (per la divisione nested/sheath).
 * Sostituisce estimateRenderableHeight in nestedCableStructure.js.
 */
export const estimateRenderableHeight = (renderable) => {
  const scale = getScale();

  if (renderable.type === 'multipolar') {
    return estimateMultipolarCableHeight(renderable.cable, scale);
  }

  if (renderable.type === 'multiple') {
    return estimateMultipleGroupHeight(renderable.cables, scale);
  }

  if (renderable.type === 'sheathSingles') {
    return estimateSheathGroupHeight(renderable.cables, scale);
  }

  return 0;
};

/**
 * Stima l'altezza di un nodo sheath dai suoi gruppi logici.
 * Sostituisce estimateSheathNodeHeight in nestedCableStructure.js.
 */
export const estimateSheathNodeHeight = (groupList) => {
  const scale = getScale();
  let height = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;

  // Separa i gruppi come fa HybridCableVisualization
  const multipolarCables = [];
  const multipleGroups = [];
  const singleCablesWithSheath = [];

  groupList.forEach(group => {
    if (group.isSingle) {
      const cable = group.cables[0];
      if (cable.poli && Array.isArray(cable.poli)) {
        multipolarCables.push(cable);
      } else {
        singleCablesWithSheath.push(cable);
      }
    } else {
      multipleGroups.push(group);
    }
  });

  // Cavi multipolari (ognuno ha il suo SVG)
  multipolarCables.forEach(cable => {
    height += estimateMultipolarCableHeight(cable, scale);
  });

  // Gruppi multipli (ogni gruppo ha il suo SVG condiviso)
  multipleGroups.forEach(group => {
    height += estimateMultipleGroupHeight(group.cables, scale);
  });

  // Cavi singoli nella guaina (TUTTI condividono UN UNICO SVG)
  if (singleCablesWithSheath.length > 0) {
    height += estimateSheathGroupHeight(singleCablesWithSheath, scale);
  }

  return height;
};

/**
 * Calcola l'altezza di un sotto-gruppo guaina con N cavi singoli.
 * Usata per determinare quanti cavi singoli entrano in una pagina durante la divisione.
 */
export const estimateSheathChunkHeight = (numSingles, scale) => {
  let height = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;
  if (numSingles > 0) {
    const svgHeight = estimateSheathSvgHeight(numSingles);
    height += (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
    height += numSingles * PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT;
  }
  return height;
};

// ==================== LETTURA DAL DOM ====================

/**
 * Legge l'altezza scalata di un SVG dal suo elemento DOM.
 * Restituisce l'altezza in mm per il PDF, tenendo conto della scala.
 */
export const readSVGHeightFromDOM = (svgElement, usableWidth = PDF_CONSTANTS.USABLE_WIDTH) => {
  const viewBox = svgElement.getAttribute('viewBox')?.split(' ');
  if (!viewBox || viewBox.length < 4) return 0;

  const svgWidth = parseFloat(viewBox[2]);
  const svgHeight = parseFloat(viewBox[3]);
  const scale = (usableWidth - 6) / svgWidth;
  return (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
};

/**
 * Calcola l'altezza di un cavo nei dettagli in base ai dati.
 */
export const estimateCableDetailHeight = (cableData) => {
  if (cableData && cableData.poli && Array.isArray(cableData.poli)) {
    return PDF_CONSTANTS.MULTIPOLAR_HEADER_HEIGHT +
      PDF_CONSTANTS.MULTIPOLAR_TABLE_HEADER +
      (cableData.poli.length * PDF_CONSTANTS.MULTIPOLAR_POLE_HEIGHT) +
      PDF_CONSTANTS.MULTIPOLAR_SEPARATOR;
  }
  return PDF_CONSTANTS.STANDARD_CABLE_DETAIL_HEIGHT;
};

/**
 * Calcola l'altezza precisa di un gruppo leggendo gli SVG dal DOM.
 * Richiede che il container schematico sia renderizzato.
 * Fallback alle stime se il DOM non è disponibile.
 * 
 * @param {Array} cables - Array di cavi nel gruppo
 * @param {Object} settings - Impostazioni dell'app
 * @param {number} usableWidth - Larghezza disponibile in mm (default: PDF_CONSTANTS.USABLE_WIDTH)
 * @param {string|null} groupIdentifier - ID del gruppo nel DOM (opzionale)
 * @returns {number} Altezza stimata del gruppo in mm
 */
export const calculateGroupHeightFromDOM = (cables, settings, usableWidth = PDF_CONSTANTS.USABLE_WIDTH, groupIdentifier = null) => {
  let groupHeight = PDF_CONSTANTS.HEADER_HEIGHT;
  const scale = getScale(usableWidth);

  // Cerca il container schematico nel DOM
  const schematicContainer = document.getElementById('schematic-container');

  if (!schematicContainer) {
    // Fallback alle stime se il DOM non è disponibile
    return estimateGroupHeightFallback(cables, scale);
  }

  // IDENTIFICA IL GRUPPO CORRETTO NEL DOM
  let targetGroup = null;

  if (groupIdentifier) {
    targetGroup = document.getElementById(`cable-group-${groupIdentifier}`);
  }

  if (!targetGroup) {
    // Fallback: cerca il gruppo che contiene i cavi specifici
    const cableGroups = schematicContainer.querySelectorAll('[id^="cable-group-"]');
    const expectedNumbers = cables.map(c => c.originalIndex);
    targetGroup = Array.from(cableGroups).find(group => {
      const cableNumbers = Array.from(group.querySelectorAll('span')).map(span =>
        parseInt(span.textContent?.replace('#', '')) - 1
      );
      return expectedNumbers.every(num => cableNumbers.includes(num));
    });
  }

  if (!targetGroup) {
    // Fallback finale: usa stime
    return estimateGroupHeightFallback(cables, scale);
  }

  // LETTURA DAL DOM: Calcola altezza SVG del gruppo specifico
  const expectedCableNumbers = cables.map(c => c.originalIndex);
  const svgs = targetGroup.querySelectorAll('.bg-gray-50.rounded-lg.p-4 svg, .bg-gray-50.rounded-lg.p-2 svg');
  let totalSvgHeight = 0;

  for (let j = 0; j < svgs.length; j++) {
    // Trova il cavo associato a questo SVG
    const svgContainer = svgs[j].closest('.bg-gray-50.rounded-lg');
    const cableIndexAttr = svgContainer?.getAttribute('data-cable-index');
    const cableIndicesAttr = svgContainer?.getAttribute('data-cable-indices');

    let cableNumbers = [];
    if (cableIndexAttr) {
      cableNumbers = [parseInt(cableIndexAttr)];
    } else if (cableIndicesAttr) {
      cableNumbers = cableIndicesAttr.split(',').map(n => parseInt(n));
    }

    // Calcola solo se almeno uno dei cavi è tra quelli richiesti
    const hasRequiredCable = cableNumbers.some(cableNum => expectedCableNumbers.includes(cableNum));
    if (hasRequiredCable) {
      totalSvgHeight += readSVGHeightFromDOM(svgs[j], usableWidth);
    }
  }
  groupHeight += totalSvgHeight;

  // LETTURA DAL DOM: Calcola altezza dettagli del gruppo specifico
  const detailsSection = targetGroup.querySelector('.bg-blue-50.rounded-lg.p-6');
  const cableDetails = detailsSection ? detailsSection.querySelectorAll('.space-y-2 > div') : [];
  let detailsHeight = 0;

  if (cableDetails.length > 0) {
    detailsHeight = PDF_CONSTANTS.DETAILS_TABLE_HEADER;

    cableDetails.forEach((cableDetailDiv) => {
      const tessera = cableDetailDiv.querySelector('div[class*="border-2"][class*="rounded-lg"]');
      if (tessera) {
        const cableIndex = parseInt(tessera.querySelector('span')?.textContent?.replace('#', '') - 1);

        if (expectedCableNumbers.includes(cableIndex)) {
          const cableData = cables.find(c => c.originalIndex === cableIndex);
          detailsHeight += estimateCableDetailHeight(cableData);
        }
      }
    });
  }

  groupHeight += detailsHeight;
  return groupHeight;
};

// ==================== FALLBACK STIME ====================

/**
 * Stima l'altezza di un gruppo quando il DOM NON è disponibile.
 * Usata come fallback da calculateGroupHeightFromDOM.
 */
const estimateGroupHeightFallback = (cables, scale) => {
  let groupHeight = PDF_CONSTANTS.HEADER_HEIGHT + PDF_CONSTANTS.DETAILS_TABLE_HEADER;
  let totalSvgHeight = 0;

  // Raggruppa i cavi per guaina
  const cablesWithSheath = cables.filter(c => c.guaina && !(c.poli && Array.isArray(c.poli)));
  const cablesWithoutSheath = cables.filter(c => !c.guaina && !(c.poli && Array.isArray(c.poli)));
  const multipolarCables = cables.filter(c => c.poli && Array.isArray(c.poli));

  const sheathGroups = {};
  cablesWithSheath.forEach(cable => {
    if (!sheathGroups[cable.guaina]) {
      sheathGroups[cable.guaina] = [];
    }
    sheathGroups[cable.guaina].push(cable);
  });

  // Multipolari
  multipolarCables.forEach(cable => {
    const svgHeight = estimateMultipolarSvgHeight(cable.poli.length);
    totalSvgHeight += (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
  });

  // Guaine
  Object.values(sheathGroups).forEach(groupCables => {
    const svgHeight = estimateSheathSvgHeight(groupCables.length);
    totalSvgHeight += (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
  });

  // Standard senza guaina
  cablesWithoutSheath.forEach(() => {
    const svgHeight = estimateStandardSvgHeight();
    totalSvgHeight += (svgHeight * scale) + PDF_CONSTANTS.SVG_PADDING;
  });

  groupHeight += totalSvgHeight;
  return groupHeight;
};
